<template>
  <v-container class="pa-0" ref="latest-filters" id="latest-filters">
    <v-tabs
      v-model="filterOptions"
      fixed-tabs
      v-show="isLatestKeywordFilterUnlocked"
    >
      <v-tab :key="filterOptions[0]"
        >Location
        <v-badge
          v-if="hasLocationFilterSet"
          title="There is an active location filter"
          bordered
          inline
          class="ml-1"
        ></v-badge
      ></v-tab>
      <v-tab :key="filterOptions[1]"
        >Keywords
        <v-badge
          v-if="hasKeywordsFilterSet"
          title="There is an active keywords filter"
          bordered
          inline
          class="ml-1"
        ></v-badge
      ></v-tab>
    </v-tabs>

    <v-tabs-items v-model="filterOptions" class="filters-items-container">
      <v-tab-item :key="filterOptions[0]">
        <v-container class="filter-inner-container pa-0">
          <v-row>
            <v-container class="regionsTitleHolder pb-0 pt-0">
              <div class="regionsTitle">
                <h4>Locations</h4>
              </div>
              <a @click="handleAllRegionsClick" v-if="allRegionsSelected">
                Deselect All
              </a>
              <a @click="handleAllRegionsClick" v-if="!allRegionsSelected">
                Select All
              </a>
            </v-container>
          </v-row>
          <v-row>
            <v-container class="regionCheckboxHolder">
              <div class="regionCheckbox pr-2">
                <v-checkbox
                  dense
                  @click="handleAllRegionsClick"
                  :value="allRegionsSelected"
                  label="All Regions"
                  hide-details
                  class="ma-0"
                />
              </div>
              <div
                class="regionCheckbox pr-2"
                v-for="(region, index) in regions"
                :key="index"
              >
                <v-checkbox
                  v-model="regionFilters"
                  :label="region.text"
                  :value="region.value"
                  hide-details
                  class="ma-0"
                  @change="handleRegionChange"
                  dense
                />
              </div>
            </v-container>
          </v-row>
          <v-row>
            <v-container pb-0 pt-0>
              <v-col cols="12" class="countrySelectCol pa-0">
                <v-autocomplete
                  :search-input.sync="inputText"
                  v-model="countryFilters"
                  label="Countries"
                  :items="countries"
                  @change="handleCountryChange"
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  clearable
                  single-line
                  dense
                />
              </v-col>
            </v-container>
          </v-row>
        </v-container>
      </v-tab-item>
      <v-tab-item :key="filterOptions[1]">
        <v-container>
          <SearchFilterKeywords
            :inherited-keywords="keywords"
            :inherited-keywords-builder="keywordsBuilder"
            @change-keywords="handleChangeKeywords"
            @change-keywords-builder="handleChangeKeywordsBuilder"
          />
        </v-container>
      </v-tab-item>
    </v-tabs-items>
  </v-container>
</template>

<script>
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

import lodashGet from "lodash/get";
import { getAllRegions } from "@/common/maps/utilities";
import { mapGetters } from "vuex";
import { MODULE_PREFIX_USER } from "@/store";
import { SAVE_PREFERENCES } from "@/store/actions.type";
import { LATEST_CATEGORIES, EVENT_ACTIONS } from "@/common/constants/analytics";
import { getKeywordsStringFromBuilder } from "@/services/search/savedSearch.service";

const EMITTED_ACTIONS = {
  CHANGE_PARAMS: "change-params",
  UPDATED: "updated",
};

export default {
  name: "LatestFilters",
  components: {
    SearchFilterKeywords: () =>
      import("@/components/search/SearchFilterKeywords"),
  },
  data() {
    return {
      filterOptions: ["location", "keywords"],
      regions: [],
      regionFilters: [],
      countryFilters: [],
      items: [],
      page: 1,
      pageSize: 25,
      timeout: null,
      inputText: "",
      keywords: null,
      keywordsBuilder: null,
      keywordsPref: null,
      keywordsBuilderPref: null,
    };
  },
  computed: {
    ...mapGetters({
      preferences: MODULE_PREFIX_USER + "preferences",
    }),
    countries() {
      let countries = [];
      this.regions?.forEach((region) => {
        countries = countries.concat(region.countries);
      });
      countries.sort((a, b) => a.text.localeCompare(b.text));
      return countries;
    },
    aoiId() {
      // if no regions set, no filter
      if (this.regionFilters.length === 0) return null;
      // if country not set, filter by region
      if (this.countryFilters.length === 0)
        return this.regionFilters.map((rf) => rf.value).join(",");
      // if country set, only include country filters to avoid inclusive or conflict
      return this.countryFilters
        .map((cf) => cf.value)
        .flat()
        .join(",");
    },
    allRegionsSelected() {
      return (
        this.regionFilters?.length === this.regions?.length &&
        this.regions?.length > 0
      );
    },
    filterResetIconSrc() {
      return require("@/assets/images/icons/reset_filters.png");
    },
    isLatestKeywordFilterUnlocked() {
      return this.featureUnlocked("latest-keyword-filter");
    },
    hasLocationFilterSet() {
      return (
        (this.countryFilters?.length || this.regionFilters?.length) &&
        !this.allRegionsSelected
      );
    },
    hasKeywordsFilterSet() {
      return (
        !!lodashGet(this.keywordsBuilder, "includes[0].keywords.length") ||
        !!lodashGet(this.keywordsBuilder, "excludes.length") ||
        !!this.keywords
      );
    },
  },
  methods: {
    async initialize() {
      this.applyPrefs();
      try {
        this.regions =
          (await getAllRegions())?.map((r) => ({
            value: r.properties.code,
            text: r.properties.name,
            countries: r.properties.countries.map((c) => ({
              value: c.isoAlpha2Code,
              text: c.name,
            })),
          })) || [];
      } catch (e) {
        console.log("Could not initialize LatestFilters", e);
      }
    },
    getParams() {
      //Date range
      let params = {
        start: new dayjs.utc().add(-30, "days").startOf("day").toISOString(),
        end: new dayjs.utc().endOf("day").toISOString(),
      };

      //Keywords filter
      // Copied from SearchContent
      //@todo: Refactor to search service?
      if (this.keywordsBuilder) {
        const keywordsBuilderString = getKeywordsStringFromBuilder(
          this.keywordsBuilder
        );
        if (keywordsBuilderString) {
          params.keywordsBuilder = this.keywordsBuilder;
          params.search = keywordsBuilderString;
        } else if (this.keywords) {
          params.search = this.keywords;
        }
      } else if (this.keywords) {
        params.search = this.keywords;
      }

      return {
        ...params,
        boundsType: this.aoiId ? "geography" : "worldwide",
        bounds: this.aoiId ? "-90,-180,90,180" : undefined,
        pageOffset: 0,
        pageSize: 25,
        severityMax: 10,
        severityMin: 0,
        aoiId: this.regionFilters.concat(this.countryFilters).join(","),
        regionFilters: this.regionFilters,
        countryFilters: this.countryFilters,
        allRegionsSelected: this.allRegionsSelected,
      };
    },
    // copied from SearchContent
    getThresholdTypeFunction(doc) {
      let thresholdType = "cluster";
      if (
        lodashGet(doc, "properties.formatted.source") === "twitter.com" ||
        lodashGet(doc, "properties.formatted.source") === "twitter"
      ) {
        thresholdType = "twitter";
      }
      return thresholdType;
    },
    handleAllRegionsClick(event) {
      if (!this.allRegionsSelected) {
        this.regionFilters = this.regions?.map((region) => {
          return region.value;
        });
        this.handlePageTrackerEvent(
          LATEST_CATEGORIES.latest_filters,
          EVENT_ACTIONS.changed,
          "All regions - selected"
        );
      } else {
        this.regionFilters = [];
        this.handlePageTrackerEvent(
          LATEST_CATEGORIES.latest_filters,
          EVENT_ACTIONS.changed,
          "All regions - deselected"
        );
      }
      event.preventDefault();
    },
    handleRegionChange(newValue) {
      this.setPrefs();
      this.handlePageTrackerEvent(
        LATEST_CATEGORIES.latest_filters,
        EVENT_ACTIONS.changed,
        `Regions - ${newValue}`
      );
    },
    handleCountryChange(newValue) {
      this.setPrefs();

      if (newValue?.length) {
        this.handlePageTrackerEvent(
          LATEST_CATEGORIES.latest_filters,
          EVENT_ACTIONS.changed,
          `Countries - ${newValue}`
        );
      } else {
        this.handlePageTrackerEvent(
          LATEST_CATEGORIES.latest_filters,
          EVENT_ACTIONS.clear,
          `Countries`
        );
      }
    },
    emitParams() {
      this.$emit(EMITTED_ACTIONS.CHANGE_PARAMS, this.getParams());
    },
    handleChangeKeywords(keywords) {
      this.keywords = keywords;

      //Turn off keyword builder if there is a regular keyword filter
      if (this.keywords) {
        this.keywordsBuilder = null;
        this.handlePageTrackerEvent(
          LATEST_CATEGORIES.latest_filters,
          EVENT_ACTIONS.changed,
          `Keywords - ${this.keywords}`
        );
      } else {
        this.handlePageTrackerEvent(
          LATEST_CATEGORIES.latest_filters,
          EVENT_ACTIONS.clear,
          `Keywords`
        );
      }

      this.setPrefs();
    },
    handleChangeKeywordsBuilder(keywordsBuilder) {
      this.keywordsBuilder = keywordsBuilder;

      let keywordBuilderString = getKeywordsStringFromBuilder(
        this.keywordsBuilder
      );

      //Turn off the regular keyword filter if there is a keyword builder filter
      if (keywordBuilderString) {
        this.keywords = null;
        this.handlePageTrackerEvent(
          LATEST_CATEGORIES.latest_filters,
          EVENT_ACTIONS.changed,
          `Keywords Builder - ${keywordBuilderString}`
        );
      } else {
        this.handlePageTrackerEvent(
          LATEST_CATEGORIES.latest_filters,
          EVENT_ACTIONS.clear,
          `Keywords Builder cleared`
        );
      }

      this.setPrefs();
    },
    applyPrefs() {
      let latestPrefs = this.preferences?.latest || {};
      if (latestPrefs.regionFilters) {
        this.regionFilters = latestPrefs.regionFilters;
      }
      if (latestPrefs.countryFilters) {
        this.countryFilters = latestPrefs.countryFilters;
      }
      this.keywords = latestPrefs.keywords;
      this.keywordsBuilder = latestPrefs.keywordsBuilder;
    },
    setPrefs() {
      if (!this.preferences) {
        this.preferences = {};
      }
      if (!this.preferences.latest) {
        this.preferences.latest = {};
      }

      const latestPrefs = {
        regionFilters: this.regionFilters,
        countryFilters: this.countryFilters,
        keywords: this.keywords,
        keywordsBuilder: this.keywordsBuilder,
      };

      Object.assign(this.preferences.latest, latestPrefs);

      this.$store.dispatch(
        MODULE_PREFIX_USER + SAVE_PREFERENCES,
        this.preferences
      );
    },
  },
  mounted() {
    this.initialize();
  },
  updated() {
    this.$emit(EMITTED_ACTIONS.UPDATED);
  },
  watch: {
    regionFilters() {
      this.emitParams();
    },
    keywords() {
      this.emitParams();
    },
    keywordsBuilder() {
      this.emitParams();
    },
    countryFilters(cur, prev) {
      this.inputText = "";
      this.emitParams();
    },
  },
};
</script>

<style scoped>
/*
https://stackoverflow.com/a/50985784
https://vue-loader.vuejs.org/guide/scoped-css.html#deep-selectors
*/
>>> .v-input__slot .v-input--selection-controls__input {
  margin-bottom: auto;
  height: 15px;
  width: 15px;
  top: 2px;
}
>>> .v-input--selection-controls .v-input__slot > .v-label {
  font-size: 13px;
}
>>> .v-input--selection-controls .v-icon.v-icon--dense {
  font-size: 15px;
}
>>> .v-input--selection-controls.v-input--dense
  .v-input--selection-controls__ripple {
  width: 18px;
  height: 18px;
  top: calc(50% - 16px);
  left: -8px;
}
>>> .countrySelectCol .v-label {
  font-size: 13px;
}
>>> .v-application--is-ltr .v-input--selection-controls__input {
  margin-right: 4px;
}
.regionsTitle {
  color: rgba(0, 0, 0, 0.6);
}
.regionsTitleHolder {
  display: flex;
  width: 100%;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-between;
}

.regionCheckboxHolder {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
}

.regionCheckbox {
  width: 100%;
}
.filters-items-container {
  background-color: transparent;
}
.filter-inner-container {
  box-sizing: border-box;
}
::v-deep .v-badge__badge {
  height: 12px;
  min-width: 12px;
}
@media (min-width: 900px) and (max-width: 1249px) {
  .regionCheckbox {
    width: 50%;
  }
}
@media (min-width: 1250px) {
  .regionCheckbox {
    width: 33%;
    max-width: 147px;
  }
}
</style>
